import { cn, formatCurrency } from "@/lib/utils";
import { useDemoModeStore } from "@/store/demoMode";
import type { TransactionType } from "@/types";

interface AmountDisplayProps {
  amount: number;
  currencyCode?: string;
  type?: TransactionType;
  className?: string;
  /** Keep an explicit sign when the caller renders the amount in isolation
   * and the sign carries meaning a bare negative would blur (gain/loss chips).
   * `true` derives it from `type` (credit +, debit −); `"derive"` from the
   * amount's own sign. The default keeps `−` for negative amounts and
   * renders positive amounts bare, matching the old formatCurrency output. */
  showSign?: boolean | "derive";
  /** Whole-rupee figures (no paise) for compact table contexts such as
   * budgets. Demo scrambling still applies; only the fraction digits drop. */
  compact?: boolean;
}

/**
 * formatCurrency for the rare string context that cannot render JSX
 * (native select options, chart formatters, tooltips). The third argument
 * is deliberately required: state the demo-mode choice at the call site —
 * pass the live `useDemoModeStore()` value from a component, or `false`
 * only when the string provably never reaches a user screen.
 */
export function amountString(amount: number, currency = "INR", isDemoMode: boolean): string {
  return formatCurrency(amount, currency, isDemoMode);
}

/**
 * AmountDisplay — the single rendering path for money in JSX.
 *
 * Reads demo mode itself so no call site can forget it (the forgotten-
 * `isDemoMode` class this component exists to kill). `formatCurrency` is
 * reserved for non-React contexts (see its boundary comment in
 * `lib/utils.ts`); the `check:amounts` gate keeps it out of `.tsx` files.
 *
 * Typographic styling is inherited from the parent (size, weight, tabular
 * numerals, tone). Only the transaction-type coloring (and credit emphasis)
 * is applied here.
 *
 * @example
 * ```tsx
 * <AmountDisplay amount={1500} type="credit" currencyCode="INR" />
 * <AmountDisplay amount={netGain} showSign className="text-sm tabular-nums" />
 * ```
 */
function signFor(amount: number, showSign: AmountDisplayProps["showSign"], type?: TransactionType) {
  // `true` derives the sign from the transaction type; `"derive"` from the
  // amount's own sign (gain/loss chips). Both default paths keep the minus
  // for negative amounts and render positive amounts bare, matching the
  // old formatCurrency output.
  if (showSign === "derive") return amount < 0 ? "−" : "+";
  if (showSign === true) {
    if (type?.toLowerCase() === "credit") return "+";
    if (type?.toLowerCase() === "debit") return "−";
    return "";
  }
  return amount < 0 ? "−" : "";
}

export function AmountDisplay({
  amount,
  currencyCode = "INR",
  type,
  className,
  showSign = false,
  compact = false,
}: AmountDisplayProps) {
  const { enabled: isDemoMode } = useDemoModeStore();
  const formatted = formatCurrency(Math.abs(amount), currencyCode, isDemoMode);
  const formattedAmount = compact ? formatted.replace(/\.\d{2}$/, "") : formatted;

  return (
    <span
      className={cn(
        type?.toLowerCase() === "credit" && "font-semibold text-success",
        type?.toLowerCase() === "transfer" && "text-info",
        className,
      )}
    >
      {signFor(amount, showSign, type)}
      {formattedAmount}
    </span>
  );
}
